/**
 * Building and comparing transactions.
 *
 * Every path into the app -- typing, a Shortcut, a receipt, a CSV -- ends up
 * here, so the invariants (integer sen, a normalised merchant, a dedupe key, a
 * confidence score, a review status) hold no matter where the data came from.
 */
import { assertSen } from './money.js';
import { merchantKey, normalizeMerchant, sameMerchant } from './merchant.js';
import { truncateToMinute } from './time.js';
import { DEFAULT_CATEGORY_ID } from './categories.js';
import {
  isAutomatedSource,
  transactionSchema,
  type PaymentSource,
  type Transaction,
  type TransactionType,
} from './schemas.js';

/** Below this, an automated record is not trusted enough to count silently. */
export const REVIEW_CONFIDENCE_THRESHOLD = 0.8;

export interface CreateTransactionInput {
  id?: string;
  type?: TransactionType;
  amountSen: number;
  currency?: string;
  merchant?: string | null;
  /** Set when the source already gives a clean name and the raw one differs. */
  merchantOriginal?: string | null;
  categoryId?: string;
  accountLabel?: string;
  source?: PaymentSource;
  occurredAt?: string | Date;
  note?: string | null;
  importConfidence?: number;
  reviewStatus?: 'confirmed' | 'needs-review';
  now?: Date;
  uuid?: () => string;
}

function randomUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  // Deterministic-length fallback for environments without WebCrypto.
  const bytes = new Uint8Array(16);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A stable fingerprint for "the same payment".
 *
 * Time is rounded to the minute so a Shortcut fired a few seconds after a
 * receipt import still collides. The account is part of the key because the
 * same amount at the same shop on two cards is two real payments.
 */
export function buildDedupeKey(input: {
  type: TransactionType;
  amountSen: number;
  currency: string;
  merchantNormalized: string;
  occurredAt: string;
  accountLabel: string;
}): string {
  return [
    input.type,
    String(assertSen(input.amountSen)),
    input.currency.toUpperCase(),
    merchantKey(input.merchantNormalized) || 'unknown',
    truncateToMinute(input.occurredAt),
    input.accountLabel.trim().toLowerCase() || 'unknown',
  ].join('|');
}

export function createTransaction(input: CreateTransactionInput): Transaction {
  const now = input.now ?? new Date();
  const nowIso = now.toISOString();
  const uuid = input.uuid ?? randomUuid;

  const type = input.type ?? 'expense';
  const source = input.source ?? 'manual';
  const currency = (input.currency ?? 'MYR').toUpperCase();
  const amountSen = assertSen(Math.abs(input.amountSen), 'amountSen');

  const rawMerchant = (input.merchantOriginal ?? input.merchant ?? '').trim();
  const normalized = normalizeMerchant(input.merchant ?? rawMerchant) || rawMerchant;

  const occurredAt = input.occurredAt
    ? new Date(input.occurredAt).toISOString()
    : now.toISOString();
  const accountLabel = (input.accountLabel ?? '').trim() || 'Unspecified';

  const importConfidence = input.importConfidence ?? (source === 'manual' ? 1 : 0.5);
  const reviewStatus =
    input.reviewStatus ??
    (isAutomatedSource(source) && importConfidence < REVIEW_CONFIDENCE_THRESHOLD
      ? 'needs-review'
      : 'confirmed');

  const transaction: Transaction = {
    id: input.id ?? uuid(),
    type,
    amountSen,
    currency,
    merchantOriginal: rawMerchant,
    merchantNormalized: normalized,
    categoryId: input.categoryId ?? DEFAULT_CATEGORY_ID,
    accountLabel,
    source,
    occurredAt,
    note: input.note?.trim() || null,
    importConfidence,
    reviewStatus,
    dedupeKey: buildDedupeKey({
      type,
      amountSen,
      currency,
      merchantNormalized: normalized,
      occurredAt,
      accountLabel,
    }),
    createdAt: nowIso,
    updatedAt: nowIso,
    deletedAt: null,
  };

  // Parse rather than trust: this is the last gate before storage.
  return transactionSchema.parse(transaction);
}

/** Re-derive the dedupe key after an edit changed one of its inputs. */
export function withRefreshedDedupeKey(transaction: Transaction): Transaction {
  return {
    ...transaction,
    dedupeKey: buildDedupeKey({
      type: transaction.type,
      amountSen: transaction.amountSen,
      currency: transaction.currency,
      merchantNormalized: transaction.merchantNormalized,
      occurredAt: transaction.occurredAt,
      accountLabel: transaction.accountLabel,
    }),
  };
}

export interface DuplicateMatch {
  transaction: Transaction;
  /** 'exact' means identical dedupe keys; 'likely' means same amount and merchant, minutes apart. */
  kind: 'exact' | 'likely';
  minutesApart: number;
}

export interface DuplicateOptions {
  /** How far apart two records can be and still be the same payment. Default 5 minutes. */
  windowMinutes?: number;
}

/**
 * Find records that look like the same payment as `candidate`.
 * Never deletes anything: it reports, and the user decides.
 */
export function findDuplicates(
  candidate: Transaction,
  existing: readonly Transaction[],
  options: DuplicateOptions = {},
): DuplicateMatch[] {
  const windowMs = (options.windowMinutes ?? 5) * 60_000;
  const candidateTime = Date.parse(candidate.occurredAt);
  const matches: DuplicateMatch[] = [];

  for (const other of existing) {
    if (other.id === candidate.id) continue;
    if (other.deletedAt) continue;
    if (other.currency !== candidate.currency) continue;

    const delta = Math.abs(Date.parse(other.occurredAt) - candidateTime);
    const minutesApart = Math.round(delta / 60_000);

    if (other.dedupeKey === candidate.dedupeKey) {
      matches.push({ transaction: other, kind: 'exact', minutesApart });
      continue;
    }

    if (
      other.amountSen === candidate.amountSen &&
      other.type === candidate.type &&
      delta <= windowMs &&
      sameMerchant(other.merchantNormalized, candidate.merchantNormalized)
    ) {
      matches.push({ transaction: other, kind: 'likely', minutesApart });
    }
  }

  return matches.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'exact' ? -1 : 1;
    return a.minutesApart - b.minutesApart;
  });
}

/** Field-order-independent serialisation, so two devices hash a record alike. */
function canonicalForm(transaction: Transaction): string {
  const entries = Object.entries(transaction).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * Deterministic conflict resolution for sync.
 *
 * The newer `updatedAt` wins. When two devices wrote in the same millisecond
 * the id cannot break the tie -- both sides are versions of the *same* id -- so
 * the contents decide: the canonical serialisation that sorts higher wins.
 * Both devices run the same comparison on the same two records and therefore
 * reach the same answer without talking to each other.
 */
export function resolveConflict(local: Transaction, remote: Transaction): Transaction {
  if (local.id !== remote.id) {
    throw new Error('resolveConflict expects two versions of the same transaction');
  }
  if (local.updatedAt > remote.updatedAt) return local;
  if (remote.updatedAt > local.updatedAt) return remote;
  return canonicalForm(local) >= canonicalForm(remote) ? local : remote;
}

/** Does this record belong in the review inbox? */
export function needsReview(transaction: Transaction): boolean {
  return transaction.reviewStatus === 'needs-review' && !transaction.deletedAt;
}
